import {
  Component,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ErrorInfo,
  type ReactNode,
} from 'react';
import { ChatDemoAnimation } from '../ChatDemoAnimation';
import { ConversationAnalytics } from './ConversationAnalytics';
import { CustomBrand } from './CustomBrand';
import { CustomerGraph } from './CustomerGraph';
import { FitIntelligence } from './FitIntelligence';
import { GuardianProtocol } from './GuardianProtocol';
import { MetricsFlywheel } from './MetricsFlywheel';
import { SalesAssociate } from './SalesAssociate';
import { SkinIntelligence } from './SkinIntelligence';
import { VirtualTryOn } from './VirtualTryOn';
import { WebsitePersonalization } from './WebsitePersonalization';
import './ReelLoop.css';

type SceneComponent = React.FC<{ apiBaseUrl: string }>;

interface Scene {
  id: string;
  Component: SceneComponent;
  // Approximate length of ONE internal animation cycle for this component.
  // The reel plays each scene for `cycleMs * effectiveCycles` total.
  cycleMs: number;
  // Tasteful pastel page background for this scene. Snaps instantly between
  // scenes during the empty-bg hold so screen recordings have a clean cut
  // point that's easy to drop a crossfade into in post (Final Cut Pro).
  bgColor: string;
  // Per-scene cycle override. Falls back to CYCLES_PER_SCENE when absent.
  // Used to keep long-running scenes (full chat conversation, multi-brand
  // voice cycle) from hogging the booth loop.
  cycles?: number;
  // Header/subhead rendered on the left strip of the reel, mirroring the
  // closera.com card layout. Concise headline + supporting subhead.
  header: string;
  subhead: string;
  // Text color for header and subhead. Light pastel scenes use a dark slate
  // (#1F2937); the dark-blue hero uses white. Subhead inherits this color
  // and applies its own opacity in CSS.
  textColor: string;
}

// Each scene transition runs as a 4-phase state machine:
//   visible      -> dwell on the scene (cycleMs * cycles)
//   fading-out   -> active component opacity 1 -> 0 over FADE_MS, bg color UNCHANGED
//   hold         -> empty stage on the *next* scene's bg color for HOLD_MS
//                   (intended as a clean cut point in Final Cut Pro)
//   fading-in    -> next component mounts fresh and opacity 0 -> 1 over FADE_MS
// All phases run sequentially, so the screen recording shows: clean fade out,
// hard bg color cut, clean fade in. No simultaneous content+bg crossfade.
const FADE_MS = 800;
const HOLD_MS = 500;
const IDLE_HIDE_CURSOR_MS = 3000;
// Default number of full internal animation cycles each scene plays before
// advancing. Override at runtime with ?cycles=<n>, or per-scene via the
// `cycles` field on individual SCENE_REGISTRY entries.
const CYCLES_PER_SCENE = 2;

type Phase = 'visible' | 'fading-out' | 'hold' | 'fading-in';

// Page background palette: 11 distinct hue zones (warm/cool alternating in the
// default sequence). Hero matches the middle stop of ChatDemoAnimation's dark
// pastel blue gradient so its fade-out doesn't show a color shift. The other
// 10 are light pastels (~85% lightness, ~30-50% saturation) chosen for clean
// visual separation.
// Default text color for light pastel scenes -- a near-black slate that has
// strong contrast against every pastel bg without being pure #000 (softer).
const DARK_TEXT = '#1F2937';
// Hero (dark pastel blue bg) needs white text instead.
const LIGHT_TEXT = '#FFFFFF';

const SCENE_REGISTRY: Record<string, Scene> = {
  // hero and custom-brand are long single-pass narratives -- one full cycle is enough.
  hero: {
    id: 'hero',
    Component: ChatDemoAnimation,
    cycleMs: 20000,
    bgColor: '#3D4D6F', // dark pastel blue (matches chat gradient mid-stop)
    cycles: 1,
    header: 'Your 24/7 sales associate',
    subhead:
      'Our agent finds the right products from natural-language conversation, across your full catalog, driving 600% higher conversion, 36% higher AOV, and 12% larger order size.',
    textColor: LIGHT_TEXT,
  },
  'custom-brand': {
    id: 'custom-brand',
    Component: CustomBrand,
    cycleMs: 30000,
    bgColor: '#C2E8D1', // mint
    cycles: 1,
    header: 'Custom built for your brand',
    subhead:
      'Our agent is trained to become an expert on your products and brand voice, so every interaction reflects your voice and drives conversion.',
    textColor: DARK_TEXT,
  },
  'guardian-protocol': {
    id: 'guardian-protocol',
    Component: GuardianProtocol,
    cycleMs: 15000,
    bgColor: '#F8E5A8', // buttercup yellow
    header: 'Guardian Protocol',
    subhead:
      'Our proprietary safety system governs every response in real time, ensuring your AI stays accurate, trustworthy, and on brand.',
    textColor: DARK_TEXT,
  },
  'conversation-analytics': {
    id: 'conversation-analytics',
    Component: ConversationAnalytics,
    cycleMs: 15500,
    bgColor: '#BFDCEF', // sky blue
    header: 'Conversation Intelligence',
    subhead:
      'Our analytics surface the questions shoppers ask, the products they search for, and the objections that block them from buying.',
    textColor: DARK_TEXT,
  },
  'customer-graph': {
    id: 'customer-graph',
    Component: CustomerGraph,
    cycleMs: 9000,
    bgColor: '#F8C9D5', // rose pink
    header: 'Customer Graph',
    subhead:
      'Our customer graph builds a real-time understanding of every shopper, so every interaction delivers the right product, the right upsell, at exactly the right time.',
    textColor: DARK_TEXT,
  },
  'website-personalization': {
    id: 'website-personalization',
    Component: WebsitePersonalization,
    cycleMs: 19000,
    bgColor: '#D6E2A8', // pistachio
    header: 'A storefront for every shopper',
    subhead:
      'Our agent rewrites product descriptions and highlights the details each shopper cares about, lifting conversion.',
    textColor: DARK_TEXT,
  },
  'skin-intelligence': {
    id: 'skin-intelligence',
    Component: SkinIntelligence,
    cycleMs: 17500,
    bgColor: '#D4C0E8', // lavender
    header: 'Skin Intelligence',
    subhead:
      "Our agent reads each shopper's skin tone, undertone, and concerns, then pinpoints the right shade, formula, or skincare routine.",
    textColor: DARK_TEXT,
  },
  'virtual-try-on': {
    id: 'virtual-try-on',
    Component: VirtualTryOn,
    cycleMs: 13500,
    bgColor: '#FCC1A8', // salmon coral
    header: 'Virtual Try-On',
    subhead:
      'Our virtual try-on shows shoppers how a piece looks on them across colors and occasions, before they buy.',
    textColor: DARK_TEXT,
  },
  'fit-intelligence': {
    id: 'fit-intelligence',
    Component: FitIntelligence,
    cycleMs: 16000,
    bgColor: '#B8E5DD', // teal seafoam
    header: 'Fit Intelligence',
    subhead:
      "Our agent builds a fit profile from each shopper's measurements and past purchases, then recommends the right size, cutting returns.",
    textColor: DARK_TEXT,
  },
  'sales-associate': {
    id: 'sales-associate',
    Component: SalesAssociate,
    cycleMs: 14000,
    bgColor: '#EAC2DC', // lilac mauve
    header: 'Sells like your best associate',
    subhead:
      'Combines deep shopper context with personalized recommendations and messaging to turn intent into purchase.',
    textColor: DARK_TEXT,
  },
  'metrics-flywheel': {
    id: 'metrics-flywheel',
    Component: MetricsFlywheel,
    cycleMs: 12000,
    bgColor: '#C5CBEC', // periwinkle
    header: 'Turn browsing into buying',
    subhead:
      'Personalized recommendations, dynamic bundles, and guided discovery drive 600% higher conversion and 36% higher AOV.',
    textColor: DARK_TEXT,
  },
};

const DEFAULT_SEQUENCE_IDS = [
  'hero',
  'custom-brand',
  'guardian-protocol',
  'conversation-analytics',
  'customer-graph',
  'website-personalization',
  'skin-intelligence',
  'virtual-try-on',
  'fit-intelligence',
  'sales-associate',
  'metrics-flywheel',
];

interface ReelLoopProps {
  apiBaseUrl: string;
}

// Per-scene error guard: if a scene throws, we render a quiet fallback instead
// of letting the global ErrorBoundary unmount the entire reel. The reel timer
// keeps ticking, so the booth advances to the next scene normally.
class SceneErrorGuard extends Component<
  { sceneId: string; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[ReelLoop] Scene "${this.props.sceneId}" threw:`, error, info);
  }
  render() {
    if (this.state.failed) {
      return <div className="reel-scene-failed" aria-hidden="true" />;
    }
    return this.props.children;
  }
}

function readSequenceFromUrl(): Scene[] {
  const params = new URLSearchParams(window.location.search);
  const cardsParam = params.get('cards');
  const ids = cardsParam
    ? cardsParam.split(',').map(s => s.trim()).filter(Boolean)
    : DEFAULT_SEQUENCE_IDS;

  const resolved = ids
    .map(id => SCENE_REGISTRY[id])
    .filter((s): s is Scene => Boolean(s));

  // Guard: if the user typo'd every id, fall back to the default sequence so
  // the booth never shows a blank screen.
  if (resolved.length === 0) {
    return DEFAULT_SEQUENCE_IDS.map(id => SCENE_REGISTRY[id]).filter(Boolean);
  }
  return resolved;
}

// Absolute duration override: forces every scene to last exactly N ms,
// regardless of cycleMs/cyclesPerScene. Useful for fast-preview demos.
function readDurationOverride(): number | null {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('duration');
  if (!raw) return null;
  const parsed = parseInt(raw, 10);
  if (Number.isFinite(parsed) && parsed >= 2000) return parsed;
  return null;
}

// Cycle count override: how many internal animation cycles per scene.
// Default is CYCLES_PER_SCENE (3).
function readCyclesOverride(): number | null {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('cycles');
  if (!raw) return null;
  const parsed = parseInt(raw, 10);
  if (Number.isFinite(parsed) && parsed >= 1 && parsed <= 20) return parsed;
  return null;
}

export function ReelLoop({ apiBaseUrl }: ReelLoopProps) {
  const sequence = useMemo(() => readSequenceFromUrl(), []);
  const cyclesUrlOverride = useMemo(() => readCyclesOverride(), []);
  const durationOverride = useMemo(() => readDurationOverride(), []);

  // Compute the total time-on-screen for a scene. Precedence:
  //   1. ?duration=N URL param  -> exactly N ms (absolute override)
  //   2. ?cycles=N URL param    -> cycleMs * N for every scene
  //   3. scene.cycles (per-scene) -> cycleMs * scene.cycles
  //   4. default                -> cycleMs * CYCLES_PER_SCENE
  const sceneTotalMs = useCallback(
    (scene: Scene) => {
      if (durationOverride != null) return durationOverride;
      const cycles = cyclesUrlOverride ?? scene.cycles ?? CYCLES_PER_SCENE;
      return scene.cycleMs * cycles;
    },
    [durationOverride, cyclesUrlOverride],
  );

  const [activeIdx, setActiveIdx] = useState(0);
  const [phase, setPhase] = useState<Phase>('visible');

  const phaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (phaseTimerRef.current !== null) {
      clearTimeout(phaseTimerRef.current);
      phaseTimerRef.current = null;
    }
  }, []);

  // Phase machine. Re-runs every time activeIdx or phase changes; schedules
  // exactly one timer for the next transition.
  //
  //   visible     -- dwell for sceneTotalMs(active), then fade-out
  //   fading-out  -- wait FADE_MS, then enter hold
  //   hold        -- bump activeIdx (remounts next component fresh, snaps bg)
  //                  and immediately enter fading-in after HOLD_MS
  //   fading-in   -- wait FADE_MS, then return to visible
  useEffect(() => {
    if (sequence.length === 0) return;

    const active = sequence[activeIdx];

    if (phase === 'visible') {
      const dwell = sceneTotalMs(active);
      phaseTimerRef.current = setTimeout(() => setPhase('fading-out'), dwell);
    } else if (phase === 'fading-out') {
      phaseTimerRef.current = setTimeout(() => setPhase('hold'), FADE_MS);
    } else if (phase === 'hold') {
      phaseTimerRef.current = setTimeout(() => {
        // Advance to the next scene (this remounts the component so its
        // internal animation starts fresh) and immediately enter fade-in.
        setActiveIdx((i) => (i + 1) % sequence.length);
        setPhase('fading-in');
      }, HOLD_MS);
    } else if (phase === 'fading-in') {
      phaseTimerRef.current = setTimeout(() => setPhase('visible'), FADE_MS);
    }

    return () => clearTimer();
  }, [activeIdx, phase, sequence, sceneTotalMs, clearTimer]);

  // When the laptop sleeps and wakes, setTimeout can be heavily throttled and
  // the active scene may have been displayed for far longer than intended.
  // Snap to a clean state on visibility regain.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      clearTimer();
      setActiveIdx(0);
      setPhase('visible');
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [clearTimer]);

  // Cursor management: hide after IDLE_HIDE_CURSOR_MS of no movement so the
  // booth display never shows a stray pointer. Move events temporarily reveal
  // the cursor, useful when an operator interacts with the laptop.
  useEffect(() => {
    const root = document.documentElement;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;

    const reveal = () => {
      root.classList.remove('reel-cursor-hidden');
      if (idleTimer !== null) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        root.classList.add('reel-cursor-hidden');
      }, IDLE_HIDE_CURSOR_MS);
    };

    reveal();
    window.addEventListener('mousemove', reveal);
    window.addEventListener('keydown', reveal);

    return () => {
      window.removeEventListener('mousemove', reveal);
      window.removeEventListener('keydown', reveal);
      if (idleTimer !== null) clearTimeout(idleTimer);
      root.classList.remove('reel-cursor-hidden');
    };
  }, []);

  if (sequence.length === 0) return null;

  const activeScene = sequence[activeIdx];
  // During the empty 'hold' phase the bg snaps forward to the *next* scene's
  // color (the active component is invisible at opacity 0 anyway). For every
  // other phase the bg matches the currently rendered scene.
  const bgScene = phase === 'hold'
    ? sequence[(activeIdx + 1) % sequence.length]
    : activeScene;

  return (
    <div
      className="reel-loop"
      style={
        {
          ['--reel-bg' as string]: bgScene.bgColor,
          ['--reel-text-color' as string]: bgScene.textColor,
        } as React.CSSProperties
      }
    >
      <div
        key={`scene-${activeScene.id}-${activeIdx}`}
        className={`reel-layer reel-layer-${phase}`}
      >
        <SceneErrorGuard sceneId={activeScene.id}>
          <activeScene.Component apiBaseUrl={apiBaseUrl} />
        </SceneErrorGuard>
        <div className="reel-title" aria-hidden="true">
          <h1 className="reel-title-header">{activeScene.header}</h1>
          <p className="reel-title-subhead">{activeScene.subhead}</p>
        </div>
      </div>
    </div>
  );
}
